import { useEffect, useState } from "react";
import { MagnifyingGlass, ArrowSquareOut, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export const ResearchAnswer = ({ result, testid = "research-answer" }) => (
  <div data-testid={testid} className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3">
    <p className="whitespace-pre-line text-sm leading-relaxed text-[#0F172A]">{result.answer}</p>
    {result.sources?.length > 0 && (
      <div className="mt-3 border-t border-[#C7D6FF] pt-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sources</p>
        <ul className="mt-1 space-y-1" data-testid={`${testid}-sources`}>
          {result.sources.slice(0, 6).map((s, i) => (
            <li key={`${s.url}-${i}`}>
              <a href={s.url} target="_blank" rel="noopener noreferrer"
                data-testid={`${testid}-source-${i}`}
                className="tap-safe text-xs font-semibold text-[#002FA7] hover:underline">
                {s.title || s.url} <ArrowSquareOut size={11} className="inline align-baseline" />
                {s.date ? <span className="ml-1 font-normal text-slate-400">{s.date}</span> : null}
              </a>
            </li>
          ))}
        </ul>
      </div>
    )}
    <p className="mt-2 text-[11px] text-slate-400">
      Live web research{result.model ? ` · ${result.model}` : ""}{result.cached ? " · from today's cache" : ""}
    </p>
  </div>
);

export const ResearchKeyPanel = ({ onReady }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [info, setInfo] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/research/settings").then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, [isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/research/settings/key", { key: value.trim() });
      toast.success(`Research key saved (…${data.last_four}) — web research is on`);
      setValue("");
      const next = await api.get("/research/settings");
      setInfo(next.data);
      onReady?.();
    } catch (e) { oops(e); } finally { setSaving(false); }
  };

  const clear = async () => {
    try {
      await api.delete("/research/settings/key");
      const next = await api.get("/research/settings");
      setInfo(next.data);
      toast.success("Research key removed");
    } catch (e) { oops(e); }
  };

  if (!isAdmin) return null;
  if (info?.configured) {
    return (
      <p data-testid="research-key-ok" className="border-l-2 border-[#0F7A3D] bg-[#EAF7EF] px-3 py-2 text-xs text-[#0B5C2E]">
        Web research is on{info.last_four ? ` (key ending ${info.last_four})` : ""}
        {info.from_environment ? " from the server settings" : info.saved_by ? `, added by ${info.saved_by}` : ""}
        {` · ${info.model}`}. Answers are cached for 12 hours so the same question is only paid for once.
        {!info.from_environment && (
          <button data-testid="research-key-clear" onClick={clear} className="tap-safe ml-2 font-semibold underline">Remove it</button>
        )}
      </p>
    );
  }
  return (
    <div data-testid="research-key-panel" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3">
      <p className="text-sm font-semibold text-[#0F172A]">Switch on live web research</p>
      <p className="mt-1 max-w-2xl text-xs text-slate-600">
        Paste your Perplexity API key to let the app research the live web and cite its sources.
        Get one at{" "}
        <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer" className="tap-safe font-semibold text-[#002FA7] underline">
          perplexity.ai/settings/api
        </a>{" "}
        — it is billed by Perplexity, not by us.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input data-testid="research-key-input" value={value} onChange={(e) => setValue(e.target.value)}
          placeholder="pplx-…" className="sm:max-w-sm" />
        <Button data-testid="research-key-save" className="w-full sm:w-auto" onClick={save} disabled={saving || value.trim().length < 20}>
          {saving ? "Checking…" : "Save the key"}
        </Button>
      </div>
    </div>
  );
};

/** One-tap research button that drops its cited answer inline. */
export const ResearchButton = ({ label = "Research it", endpoint, body, testid, className = "" }) => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, body);
      setResult(data);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (result) return <div className="mt-3"><ResearchAnswer result={result} testid={`${testid}-answer`} /></div>;
  return (
    <button data-testid={testid} onClick={run} disabled={busy}
      className={`tap-safe inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60 ${className}`}>
      <Globe size={14} /> {busy ? "Researching…" : label}
    </button>
  );
};

const PRESETS = {
  market: ["What is driving steel prices this week?", "What is happening to ocean and air freight rates right now?", "What is the outlook for diesel prices this month?"],
  rules: ["import duty on steel fasteners", "forklift operator safety rules", "overtime and shift pay rules"],
  general: ["Which certifications do industrial suppliers usually need?", "What are typical payment terms for industrial suppliers?"],
};

export const ResearchPanel = () => {
  const [scope, setScope] = useState("market");
  const [question, setQuestion] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/research/recent?limit=8").then(({ data }) => setRecent(data)).catch(() => setRecent([]));
  useEffect(() => { loadRecent(); }, []);

  const run = async (typed) => {
    const q = (typed ?? question).trim();
    if (q.length < 4) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = scope === "rules"
        ? (await api.post("/research/rules", { topic: q, country: country.trim() || "United Kingdom" }))
        : scope === "supplier"
          ? (await api.post("/research/supplier", { name: q, country: country.trim() || null }))
          : (await api.post("/research/ask", { question: q, scope }));
      setResult(data);
      loadRecent();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="research-panel">
      <ResearchKeyPanel />

      <div className="flex gap-2 scroll-x overflow-x-auto pb-1" data-testid="research-scopes">
        {[["market", "Markets & news"], ["supplier", "Check a company"], ["rules", "Rules & duties"], ["general", "Anything else"]].map(([key, label]) => (
            <button key={key} data-testid={`research-scope-${key}`} onClick={() => { setScope(key); setResult(null); }}
            className={`tap-safe whitespace-nowrap border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
              scope === key ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-border bg-white text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"}`}>
            {label}
          </button>
        ))}
      </div>

      <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(e) => { e.preventDefault(); run(); }}>
        <Input data-testid="research-question" value={question} onChange={(e) => setQuestion(e.target.value)}
          placeholder={scope === "supplier" ? "Company name" : scope === "rules" ? "What rule are you checking?" : "Ask a question about the live world"} />
        {(scope === "rules" || scope === "supplier") && (
          <Input data-testid="research-country" value={country} onChange={(e) => setCountry(e.target.value)}
            placeholder="Country" className="sm:max-w-[12rem]" />
        )}
        <Button type="submit" className="w-full sm:w-auto" data-testid="research-send" disabled={busy || question.trim().length < 4}>
          <MagnifyingGlass size={16} className="mr-1.5" /> {busy ? "Researching…" : "Research"}
        </Button>
      </form>

      {!result && PRESETS[scope] && (
        <div className="flex flex-wrap gap-2" data-testid="research-presets">
          {PRESETS[scope].map((p, i) => (
            <button key={p} data-testid={`research-preset-${i}`} onClick={() => { setQuestion(p); run(p); }}
              className="border border-border px-3 py-2 text-xs text-slate-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">
              {p}
            </button>
          ))}
        </div>
      )}

      {busy && <p className="text-xs text-slate-400" data-testid="research-thinking">Reading the web…</p>}
      {result && <ResearchAnswer result={result} />}

      {recent.length > 0 && (
        <div className="border border-border bg-white p-4" data-testid="research-recent">
          <p className="text-sm font-semibold text-[#0F172A]">Looked up lately</p>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            {recent.map((r, i) => (
              <li key={i} data-testid={`research-recent-${i}`}>
                <button className="tap-safe text-left hover:text-[#002FA7]" onClick={() => { setQuestion(r.question); run(r.question); }}>
                  {r.question}
                </button>
                {r.user_name ? <span className="text-slate-400"> · {r.user_name}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
